/**
 * Custom Variant Picker - Universal Handler
 * Handles clicks and interactions for custom variant cards
 */

document.addEventListener('DOMContentLoaded', function() {
  // Wait a bit for variant-selects to be fully rendered
  setTimeout(() => {
    const variantSelects = document.querySelectorAll('variant-selects');
    
    variantSelects.forEach(variantSelect => {
      initializeVariantPicker(variantSelect);
    });
    
    // Fetch unit prices from product JSON
    fetchUnitPricesFromProduct();

    // 2026-05-21: convert the "Size: 30 ml" label above the variant picker for US market.
    // Watches for changes so it stays converted when the visitor switches variant.
    setupUsImperialSizeTitle();

    // 2026-05-24: re-render unit prices when the header VAT INC/EXC pill is clicked.
    // Click-delegated so it survives header re-renders. 50ms timeout lets the existing
    // pill handler write the new mode to localStorage before we read it.
    document.addEventListener('click', function(e) {
      if (e.target.closest && e.target.closest('.vat-pill-btn')) {
        setTimeout(function () {
          renderVariantCards(); // PDP
          if (currentQuickAddData && currentQuickAddModal) {
            renderVariantCards(currentQuickAddData, currentQuickAddModal);
          }
        }, 50);
      }
    });

    // 2026-05-24: enhance ConvertCart quick-add modal — same per-unit-price + US-imperial
    // + VAT-aware logic that runs on PDPs. The modal is loaded lazily by CC so we hook the
    // .cc-quick-btn click, fetch the product data, wait for the modal, then render into it.
    setupQuickAddModalEnhancer();
  }, 100);
});

// Module-level refs so the VAT toggle handler can re-render the open modal too.
let currentQuickAddData = null;
let currentQuickAddModal = null;

function setupQuickAddModalEnhancer() {
  document.addEventListener('click', async function (e) {
    const btn = e.target.closest && e.target.closest('.cc-quick-btn');
    if (!btn) return;

    const href = btn.getAttribute('href') || '';
    const m = href.match(/\/products\/([^?#/]+)/);
    if (!m) return;
    const handle = m[1];

    // Fetch product data in parallel with the modal opening.
    let productData;
    try {
      const r = await fetch(`/products/${handle}.js`);
      if (!r.ok) return;
      productData = await r.json();
    } catch (err) { return; }

    // Poll for the visible modal (CC injects + toggles cc-quickview-show).
    let modal = null;
    for (let i = 0; i < 25; i++) {
      await new Promise(res => setTimeout(res, 100));
      modal = document.querySelector('[class*="cc-quick-view"].cc-quickview-show');
      if (modal && modal.querySelector('.custom-variant-card__price-per-unit, .custom-variant-card__name')) break;
    }
    if (!modal) return;
    // Small grace period for any final CC content swap.
    await new Promise(res => setTimeout(res, 150));

    currentQuickAddData = productData;
    currentQuickAddModal = modal;

    renderVariantCards(productData, modal);

    // Also convert the "Size: 30 ml" title inside the modal for US visitors.
    const titleSpan = modal.querySelector('.custom-variant-picker__title .form__label__value');
    if (titleSpan) {
      const converted = convertMetricSizeForUS(titleSpan.textContent);
      if (converted) titleSpan.textContent = converted;
    }

    // When the modal closes (CC removes cc-quickview-show), clear our refs.
    const closeObserver = new MutationObserver(function () {
      if (!modal.classList.contains('cc-quickview-show')) {
        if (currentQuickAddModal === modal) {
          currentQuickAddData = null;
          currentQuickAddModal = null;
        }
        closeObserver.disconnect();
      }
    });
    closeObserver.observe(modal, { attributes: true, attributeFilter: ['class'] });
  });
}

/**
 * Convert a metric size string ("30 ml" / "100 g") to imperial+metric for US visitors.
 * Returns null when the input doesn't match a convertible pattern or market isn't US.
 */
function convertMetricSizeForUS(text) {
  const activeCurrency = (window.theme && window.theme.settings && window.theme.settings.active_currency) || 'GBP';
  if (activeCurrency !== 'USD' || !text) return null;
  const trimmed = text.trim();
  const ml = trimmed.match(/^(\d+(?:\.\d+)?)\s*ml$/i);
  if (ml) {
    const v = parseFloat(ml[1]);
    return `${(v * 0.033814).toFixed(2)} fl oz (${v} ml)`;
  }
  const g = trimmed.match(/^(\d+(?:\.\d+)?)\s*g$/i);
  if (g) {
    const v = parseFloat(g[1]);
    return `${(v * 0.035274).toFixed(2)} oz (${v} g)`;
  }
  return null;
}

function setupUsImperialSizeTitle() {
  const valueSpans = document.querySelectorAll('.custom-variant-picker__title .form__label__value');
  valueSpans.forEach(span => {
    // Initial conversion
    const converted = convertMetricSizeForUS(span.textContent);
    if (converted) span.textContent = converted;

    // Re-convert when the picker updates this span (variant switch). The regex in
    // convertMetricSizeForUS only matches pure "X ml/g" so already-converted text
    // is skipped and we avoid infinite loops.
    const observer = new MutationObserver(() => {
      const next = convertMetricSizeForUS(span.textContent);
      if (next && next !== span.textContent.trim()) span.textContent = next;
    });
    observer.observe(span, { childList: true, characterData: true, subtree: true });
  });
}

// 2026-05-24: cache the productData fetch so the VAT toggle can re-render without re-fetching.
let cachedProductData = null;

/**
 * Multiplier to apply to the stored unit_price for the current INC/EXC toggle.
 *
 * variant.unit_price is the EX-VAT base — same basis as variant.price (see
 * custom-product-price.liquid: inc = price + price*vat_rate). So this mirrors the
 * main price: INCLUSIVE adds VAT, EXCLUSIVE shows the base. (The previous version
 * had this inverted, which took ~20% off in every mode.)
 *
 *  - T0 (zero-rated) products never have VAT → always 1
 *  - Non-tax markets (e.g. USD) show the base with no VAT → always 1 (mirrors
 *    is_tax_market in custom-product-price.liquid)
 *  - Mode from localStorage 'taxify-vat-mode' (set by the header INC/EXC pill),
 *    default inclusive
 */
function getVatModeMultiplier(taxCode) {
  if (taxCode === 'T0') return 1;
  // VAT only applies where Aromantic collects it (GBP = UK VAT, EUR + EU non-EUR = IOSS).
  var TAX_CURRENCIES = ['GBP', 'EUR', 'BGN', 'CZK', 'DKK', 'HUF', 'PLN', 'RON', 'SEK'];
  var currency = (window.theme && window.theme.settings && window.theme.settings.active_currency) || 'GBP';
  if (TAX_CURRENCIES.indexOf(currency) === -1) return 1;
  const rate = (window.theme && window.theme.settings && window.theme.settings.vat_rate);
  if (!rate || isNaN(rate)) return 1;
  const mode = (typeof localStorage !== 'undefined' && localStorage.getItem('taxify-vat-mode')) || 'inclusive';
  if (mode === 'exclusive') return 1;          // show the EX-VAT base
  return 1 + parseFloat(rate);                  // inclusive → add VAT
}

/**
 * Fetch unit price measurements from Shopify product.json
 */
async function fetchUnitPricesFromProduct() {
  const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
  if (!productHandle) {
     return;
  }

  try {
    const response = await fetch(`/products/${productHandle}.js`);
    if (!response.ok) {
      console.warn('❌ Failed to fetch product.js:', response.status);
      return;
    }

    cachedProductData = await response.json();
    renderVariantCards();
    addVariantSaleBadges(cachedProductData, document);
  } catch (error) {
    console.error('❌ Error fetching unit prices:', error);
  }
}

function addVariantSaleBadges(productData, container) {
  productData = productData || cachedProductData;
  container = container || document;
  if (!productData || !Array.isArray(productData.variants)) return;
  productData.variants.forEach(function (variant) {
    if (!variant.compare_at_price || variant.compare_at_price <= variant.price) return;
    const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
    // 2026-05-25: tighten selector to only the variant CARDS — the Swym wishlist
    // button also carries data-variant-id and was being mistaken for a card.
    const cards = container.querySelectorAll('.custom-variant-card[data-variant-id="' + variant.id + '"]');
    cards.forEach(function (card) {
      if (card.querySelector('.aro-qa-variant-sale-badge')) return;
      const badge = document.createElement('span');
      badge.className = 'aro-qa-variant-sale-badge';
      badge.textContent = pct + '% off';
      badge.style.cssText = 'position:absolute;top:-10px;right:8px;background:#a81f51;color:#fff;font-size:10px;font-weight:600;padding:4px 7px;border-radius:3px;line-height:1;letter-spacing:0.02em;z-index:2;pointer-events:none;box-shadow:0 1px 3px rgba(0,0,0,0.15);';
      const cs = getComputedStyle(card);
      if (cs.position === 'static') card.style.position = 'relative';
      card.style.overflow = 'visible';
      const wrapper = card.closest('.custom-variant-card-wrapper');
      if (wrapper) wrapper.style.overflow = 'visible';
      // The tile row (.custom-variant-cards) scrolls horizontally (overflow-x:auto),
      // which forces overflow-y:hidden and clips the badge that sits at top:-10px —
      // on mobile you'd only see the bottom of it. overflow-y can't be made visible
      // while overflow-x is auto, so give the row top padding to make room instead.
      const row = card.closest('.custom-variant-cards');
      if (row && (parseFloat(getComputedStyle(row).paddingTop) || 0) < 14) {
        row.style.paddingTop = '14px';
      }
      card.appendChild(badge);
    });
  });
}

/**
 * Render unit-price + size-label content on every visible variant card.
 * Defaults to cachedProductData scoped to the whole document (for PDPs).
 * Pass (productData, container) explicitly to render inside the ConvertCart
 * quick-add modal or anywhere else that injects variant cards.
 */
function renderVariantCards(productData, container) {
  productData = productData || cachedProductData;
  container = container || document;
  if (!productData) return;

  productData.variants.forEach(variant => {
    if (!variant.unit_price_measurement) return;

    const cards = container.querySelectorAll(`[data-variant-id="${variant.id}"]`);
    cards.forEach(card => {
      const pricePerUnitEl = card.querySelector('.custom-variant-card__price-per-unit');
      if (pricePerUnitEl && variant.unit_price) {
          // 2026-05-21: read active market currency + country from theme global
          // (set by theme.liquid from localization.country.*).
          const activeCurrency = (window.theme && window.theme.settings && window.theme.settings.active_currency)
            || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
            || 'GBP';
          const activeCountry = (window.theme && window.theme.settings && window.theme.settings.active_country) || 'GB';

          const refValue = variant.unit_price_measurement.reference_value;
          const refUnit = variant.unit_price_measurement.reference_unit;
          const refUnitLower = (refUnit || '').toLowerCase();

          // 2026-05-21: US market shows unit prices per fl oz / oz instead of metric.
          // Detect via currency (USD) — Shopify's localization.country.iso_code is unreliable
          // in dev-theme preview. Gate purely on refUnit (ml/g) — more reliable than measured_type
          // which Shopify can omit or vary.
          const useImperial = activeCurrency === 'USD';
          let displayAmount, displayLabel;
          if (useImperial && refUnitLower === 'ml') {
            displayAmount = (variant.unit_price / 100) / refValue * 29.5735;
            displayLabel = '/fl oz';
          } else if (useImperial && refUnitLower === 'g') {
            displayAmount = (variant.unit_price / 100) / refValue * 28.3495;
            displayLabel = '/oz';
          } else {
            displayAmount = variant.unit_price / 100;
            displayLabel = `/${refValue}${refUnit}`;
          }

          // Respect the header INC/EXC VAT pill toggle. Stored variant.unit_price is the
          // EX-VAT base (same basis as variant.price), so INCLUSIVE adds VAT and EXCLUSIVE
          // shows the base. T0 / non-tax markets are unaffected (see getVatModeMultiplier).
          const taxCode = card.dataset.taxCode || '';
          displayAmount = displayAmount * getVatModeMultiplier(taxCode);

          // 2026-05-24: 2 decimals by default. Only step up to 3 when the value is so
          // small that 2 decimals would round to 0.00 (e.g. fractions of a cent / penny).
          const needsExtra = displayAmount > 0 && displayAmount < 0.005;
          const formatted = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: activeCurrency,
            minimumFractionDigits: needsExtra ? 3 : 2,
            maximumFractionDigits: needsExtra ? 3 : 2,
          }).format(displayAmount);

          pricePerUnitEl.innerHTML = `<strong>${formatted}</strong> ${displayLabel}`;
        }

        // 2026-05-21: also convert the variant size label (e.g. "30 ml" → "1.01 fl oz")
        // for US market. Uses unit_price_measurement.quantity_value/quantity_unit which
        // is the canonical "what's in the bottle" amount. Reads currency directly from the
        // theme global since this block sits outside the unit-price if-scope.
        const sizeActiveCurrency = (window.theme && window.theme.settings && window.theme.settings.active_currency) || 'GBP';
        if (sizeActiveCurrency === 'USD' && variant.unit_price_measurement) {
          const nameEl = card.querySelector('.custom-variant-card__name');
          if (nameEl) {
            const qVal = parseFloat(variant.unit_price_measurement.quantity_value);
            const qUnit = (variant.unit_price_measurement.quantity_unit || '').toLowerCase();
            if (!isNaN(qVal)) {
              // 2026-05-24: imperial only on variant cards — keeps the cards visually clean.
              // The full "1.01 fl oz (30 ml)" form is still shown in the title above the picker,
              // which acts as the source of truth for the selected variant.
              if (qUnit === 'ml') {
                nameEl.textContent = `${(qVal * 0.033814).toFixed(2)} fl oz`;
              } else if (qUnit === 'g') {
                nameEl.textContent = `${(qVal * 0.035274).toFixed(2)} oz`;
              }
            }
          }
        }
      });
    });
}

/**
 * Update global stock bar based on selected variant card
 */
function updateGlobalStockBar(selectedCard) {
  const globalStockEl = document.querySelector('.custom-variant-global-stock');
  if (!globalStockEl) return;

  const variantStock = parseInt(selectedCard.dataset.variantStock) || 0;
  const stockPercent = parseInt(selectedCard.dataset.stockPercent) || 0;

  let stockStatus = 'medium';
  let stockText = 'Low stock';

  if (variantStock === 0) {
    stockStatus = 'low';
    stockText = 'Out of stock';
  } else if (stockPercent > 30) {
    stockStatus = 'high';
    stockText = 'In stock';
  }

  const textEl = globalStockEl.querySelector('.custom-variant-global-stock__text');
  const fillEl = globalStockEl.querySelector('.custom-variant-global-stock__fill');

  if (textEl) {
    textEl.className = `custom-variant-global-stock__text ${stockStatus}`;
    const spanEl = textEl.querySelector('span');
    if (spanEl) {
      spanEl.textContent = `${stockText} (${variantStock})`;
    }
  }

  if (fillEl) {
    fillEl.className = `custom-variant-global-stock__fill ${stockStatus}`;
    fillEl.style.width = `${stockPercent}%`;
  }
}

/**
 * Update sticky bar sync after variant selection
 */
function updateStickySync() {
  // Update sticky button text if function exists (defined in sticky bar snippet)
  if (typeof window.updateStickyCardsFunction === 'function') {
    window.updateStickyCardsFunction();
  }
}

function initializeVariantPicker(variantSelect) {
  const filterCards = variantSelect.querySelector('[data-filter-cards]');
  const detailCards = variantSelect.querySelector('[data-detail-cards]');
  const singleLevelCards = variantSelect.querySelector('[data-single-level-cards]');
  
  const filterSelect = variantSelect.querySelector('[data-filter-select]');
  const detailSelect = variantSelect.querySelector('[data-detail-select]');
  const singleDetailSelect = variantSelect.querySelector('[data-single-detail-select]');

  // Listen for clicks from sticky popup
  document.addEventListener('click', function(e) {
    const stickyCard = e.target.closest('.sticky-variants-popup .custom-variant-card');
    if (stickyCard) {
      const variantId = stickyCard.getAttribute('data-variant-id');
      // Find and select the corresponding main card
      const mainCard = document.querySelector('.custom-variant-cards .custom-variant-card[data-variant-id="' + variantId + '"]');
      if (mainCard && !mainCard.classList.contains('selected')) {
        mainCard.click();
      }
    }
  });

  // TWO LEVEL MODE: Filter + Detail
  if (filterCards && detailCards && filterSelect && detailSelect) {
    setupTwoLevelPicker(variantSelect, filterCards, detailCards, filterSelect, detailSelect);
  }
  
  // SINGLE LEVEL MODE: Only Detail
  if (singleLevelCards && singleDetailSelect) {
    setupSingleLevelPicker(variantSelect, singleLevelCards, singleDetailSelect);
  }
}

/**
 * Setup Two-Level Picker (Filter + Detail)
 */
function setupTwoLevelPicker(variantSelect, filterCards, detailCards, filterSelect, detailSelect) {
  // Handle filter card clicks
  const filterButtons = filterCards.querySelectorAll('.custom-variant-card--filter');
  
  filterButtons.forEach(button => {
    button.addEventListener('click', function(e) {
      e.preventDefault();
      const filterValue = this.getAttribute('data-filter');
      
      // Update selected state on filter cards
      filterButtons.forEach(btn => btn.classList.remove('selected'));
      this.classList.add('selected');
      
      // Update hidden select
      filterSelect.value = filterValue;
      
      // Update label
      const filterLabel = variantSelect.querySelector('[data-filter-value]');
      if (filterLabel) {
        filterLabel.textContent = filterValue;
      }
      
      // Show/hide detail cards based on filter
      filterDetailCards(detailCards, filterValue);
      
      // Select first available detail variant for this filter
      selectFirstDetailVariant(detailCards, filterValue, detailSelect);
      
      // Update sticky bar sync
      updateStickySync();
      
      // IMPORTANT: Wait for DOM to update before calling onVariantChange
      setTimeout(() => {
        if (typeof variantSelect.onVariantChange === 'function') {
          variantSelect.onVariantChange();
        } else if (typeof variantSelect.updateOptions === 'function' && typeof variantSelect.updateMasterId === 'function') {
          variantSelect.updateOptions();
          variantSelect.updateMasterId();
        } else {
          const finalChangeEvent = new Event('change', { bubbles: true });
          variantSelect.dispatchEvent(finalChangeEvent);
        }
      }, 0);
    });
  });
  
  // Handle detail card clicks
  const detailLabels = detailCards.querySelectorAll('.custom-variant-card--detail');
  
  detailLabels.forEach(label => {
    label.addEventListener('click', function(e) {
      const input = this.querySelector('input[type="radio"]');
      if (input) {
        // IMPORTANT: Mark input as checked FIRST
        input.checked = true;
        
        // Get the filter value from this card
        const cardFilterValue = this.getAttribute('data-filter');
        
        // Make sure we're in the right filter context
        if (cardFilterValue && filterSelect.value !== cardFilterValue) {
          filterSelect.value = cardFilterValue;
          
          // Update filter UI
          const filterButtons = filterCards.querySelectorAll('.custom-variant-card--filter');
          filterButtons.forEach(btn => {
            if (btn.getAttribute('data-filter') === cardFilterValue) {
              btn.classList.add('selected');
            } else {
              btn.classList.remove('selected');
            }
          });
        }
        
        // Update selected state
        detailLabels.forEach(lbl => lbl.classList.remove('selected'));
        this.classList.add('selected');
        
        // Update global stock bar
        updateGlobalStockBar(this);
        
        // Update sticky bar sync
        updateStickySync();
        
        // Update hidden select
        detailSelect.value = input.value;
        
        // Update label
        const detailLabel = variantSelect.querySelector('[data-detail-value]');
        if (detailLabel) {
          detailLabel.textContent = input.value;
        }
        
        // IMPORTANT: Wait for DOM to update before calling onVariantChange
        setTimeout(() => {
          if (typeof variantSelect.onVariantChange === 'function') {
            variantSelect.onVariantChange();
          } else if (typeof variantSelect.updateOptions === 'function' && typeof variantSelect.updateMasterId === 'function') {
            variantSelect.updateOptions();
            variantSelect.updateMasterId();
          } else {
            const finalChangeEvent = new Event('change', { bubbles: true });
            variantSelect.dispatchEvent(finalChangeEvent);
          }
        }, 0);
      }
    });
  });
  
  // Initialize: Show only cards for selected filter
  let selectedFilter = filterSelect.value;
  
  // If no value, get the first option's value
  if (!selectedFilter) {
    const firstOption = filterSelect.querySelector('option[selected], option:first-child');
    if (firstOption) {
      selectedFilter = firstOption.value;
      filterSelect.value = selectedFilter;
    }
  }
  
  if (selectedFilter) {
    filterDetailCards(detailCards, selectedFilter);
    
    // Ensure a detail card is selected
    const selectedDetailCard = detailCards.querySelector('.custom-variant-card--detail.selected');
    if (!selectedDetailCard) {
      selectFirstDetailVariant(detailCards, selectedFilter, detailSelect);
    }
  }
}

/**
 * Setup Single-Level Picker (Only Detail)
 */
function setupSingleLevelPicker(variantSelect, singleLevelCards, singleDetailSelect) {
  const detailLabels = singleLevelCards.querySelectorAll('.custom-variant-card');
  
  detailLabels.forEach(label => {
    label.addEventListener('click', function(e) {
      const input = this.querySelector('input[type="radio"]');
      if (input) {
        // IMPORTANT: Mark input as checked FIRST
        input.checked = true;
        
        // Update selected state
        detailLabels.forEach(lbl => lbl.classList.remove('selected'));
        this.classList.add('selected');
        
        // Update global stock bar
        updateGlobalStockBar(this);
        
        // Update sticky bar sync
        updateStickySync();
        
        // Update hidden select
        singleDetailSelect.value = input.value;
        
        // Update label
        const singleLabel = variantSelect.querySelector('[data-single-value]');
        if (singleLabel) {
          singleLabel.textContent = input.value;
        }
        
        // IMPORTANT: Wait for DOM to update before calling onVariantChange
        setTimeout(() => {
          if (typeof variantSelect.onVariantChange === 'function') {
            variantSelect.onVariantChange();
          } else if (typeof variantSelect.updateOptions === 'function' && typeof variantSelect.updateMasterId === 'function') {
            variantSelect.updateOptions();
            variantSelect.updateMasterId();
          } else {
            const finalChangeEvent = new Event('change', { bubbles: true });
            variantSelect.dispatchEvent(finalChangeEvent);
          }
        }, 0);
      }
    });
  });
}

/**
 * Filter detail cards based on selected filter value
 */
function filterDetailCards(detailCards, filterValue) {
  const allCardWrappers = detailCards.querySelectorAll('.custom-variant-card-wrapper');
  
  allCardWrappers.forEach(wrapper => {
    const label = wrapper.querySelector('.custom-variant-card--detail');
    if (label) {
      const cardFilterValue = label.getAttribute('data-filter');
      
      if (cardFilterValue === filterValue) {
        wrapper.style.display = '';
      } else {
        wrapper.style.display = 'none';
      }
    }
  });
}

/**
 * Select first available detail variant for the selected filter
 */
function selectFirstDetailVariant(detailCards, filterValue, detailSelect) {
  const visibleCards = Array.from(detailCards.querySelectorAll('.custom-variant-card-wrapper'))
    .filter(wrapper => {
      const label = wrapper.querySelector('.custom-variant-card--detail');
      return label && label.getAttribute('data-filter') === filterValue;
    });
  
  if (visibleCards.length > 0) {
    // Find first available (not disabled) card
    const firstAvailable = visibleCards.find(wrapper => {
      const label = wrapper.querySelector('.custom-variant-card--detail');
      return label && !label.classList.contains('disabled');
    });
    
    const cardToSelect = firstAvailable || visibleCards[0];
    const label = cardToSelect.querySelector('.custom-variant-card--detail');
    const input = label.querySelector('input[type="radio"]');
    
    if (input) {
      // Update selected state
      detailCards.querySelectorAll('.custom-variant-card--detail').forEach(lbl => {
        lbl.classList.remove('selected');
      });
      label.classList.add('selected');
      
      // Update global stock bar
      updateGlobalStockBar(label);
      
      // Update sticky bar sync
      updateStickySync();
      
      // Check the radio button
      input.checked = true;
      
      // Update hidden select
      detailSelect.value = input.value;
      
      // Update label
      const detailLabel = detailCards.closest('variant-selects').querySelector('[data-detail-value]');
      if (detailLabel) {
        detailLabel.textContent = input.value;
      }
    }
  }
}

/**
 * Trigger variant change event
 */
function triggerVariantChange(variantSelect) {
  const allSelects = variantSelect.querySelectorAll('select[data-option-position]');
  
  const changeEvent = new Event('change', { bubbles: true });
  variantSelect.dispatchEvent(changeEvent);
}
